/**
 * A foto do equipamento preparada NO NAVEGADOR, antes de subir (ADR-007,
 * "Imagens": o navegador prepara, o servidor confere).
 *
 * ## Por que aqui, e nao no servidor
 *
 * Reduzir e gerar a miniatura exige decodificar a imagem. No servidor isso
 * pediria `sharp` (dependencia nativa, que pesa na imagem Docker) ou um
 * decodificador em JavaScript puro, que travaria o fio da API por segundos
 * numa foto de 12 megapixels. O navegador ja decodifica de graca, com o
 * aparelho de quem envia. O servidor so confere: tipo pelos bytes, medidas
 * pelo cabecalho do JPEG, e tira o metadado que sobrar.
 *
 * ## O que a preparacao faz
 *
 *  - ENDIREITA: a orientacao do EXIF e aplicada ao desenhar — a foto de
 *    celular deitada sai em pe.
 *  - REDUZ o lado maior a 2.048 px, nunca aumentando.
 *  - REGRAVA em JPEG sobre fundo branco. Regravar descarta todo metadado: o
 *    EXIF, a localizacao do GPS e o modelo do aparelho nao saem daqui.
 *  - GERA A MINIATURA: o quadrado do centro, 64 x 64 px — a tabela desenha
 *    32 px, e 64 e a densidade de tela 2x.
 *
 * ## As tres medidas sao do catalogo
 *
 * `LADO_MAXIMO_DA_FOTO`, `LADO_MINIMO_DA_FOTO` e `LADO_DA_MINIATURA` repetem
 * o recurso `foto-do-equipamento` de `backend/src/armazenamento/chave.ts`, e
 * ficam escritas por extenso, uma por linha, DE PROPOSITO: o doctor (H16) as
 * le como texto e reprova se divergirem do catalogo. A tela que reduz para
 * 2.048 enquanto o servidor aceita 1.600 so descobre na hora de salvar.
 */
import { formatarDecimal, formatarInteiro } from '@/lib/formato';

export const LADO_MAXIMO_DA_FOTO = 2048;
export const LADO_MINIMO_DA_FOTO = 256;
export const LADO_DA_MINIATURA = 64;

/**
 * O mesmo teto do servidor: 5 MB. Conferir aqui poupa subir para ouvir "não".
 * Escrito como o `limiteBytes` do catalogo, em produto literal: o doctor (H16)
 * compara os dois.
 */
export const LIMITE_DA_FOTO_BYTES = 5 * 1024 * 1024;

export const QUALIDADE_JPEG = 0.85;

/**
 * Se a foto reduzida ainda passar de 5 MB na qualidade padrao, tenta mais
 * duas, e so entao desiste. Numa foto de 2.048 px isso quase nunca acontece.
 */
const QUALIDADES = [QUALIDADE_JPEG, 0.75, 0.65] as const;

/** A frase de quando o navegador nao consegue abrir o arquivo escolhido. */
export const NAO_ABRE = 'Não consegui abrir esta imagem. Envie a foto em JPEG ou PNG.';

/**
 * Um problema da imagem que a tela ja sabe explicar. A mensagem e frase
 * completa, com o que fazer em seguida, e vai direto para a tela.
 */
export class ProblemaDaFoto extends Error {
  constructor(mensagem: string) {
    super(mensagem);
    this.name = 'ProblemaDaFoto';
  }
}

export interface Medidas {
  readonly largura: number;
  readonly altura: number;
}

/**
 * As medidas da foto depois de reduzida: o lado maior vai a `ladoMaximo`,
 * mantendo a proporcao. Foto menor que isso fica como esta — aumentar so
 * inventaria pixel.
 */
export function medidasDaFoto(largura: number, altura: number, ladoMaximo: number = LADO_MAXIMO_DA_FOTO): Medidas {
  const maior = Math.max(largura, altura);
  if (maior <= ladoMaximo) return { largura, altura };
  const escala = ladoMaximo / maior;
  return {
    largura: Math.max(1, Math.round(largura * escala)),
    altura: Math.max(1, Math.round(altura * escala)),
  };
}

export interface Recorte {
  readonly x: number;
  readonly y: number;
  readonly lado: number;
}

/** O maior quadrado do CENTRO da imagem — a miniatura da lista. */
export function recorteQuadrado(largura: number, altura: number): Recorte {
  const lado = Math.min(largura, altura);
  return {
    x: Math.floor((largura - lado) / 2),
    y: Math.floor((altura - lado) / 2),
    lado,
  };
}

/** "2.048 × 1.536 px" */
export function descreverMedidas(largura: number, altura: number): string {
  return `${formatarInteiro(largura)} × ${formatarInteiro(altura)} px`;
}

/** "812 KB", "1,4 MB" — a unidade que a pessoa reconhece. */
export function descreverTamanho(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${formatarDecimal(bytes / (1024 * 1024), 1)} MB`;
  return `${formatarInteiro(Math.max(1, Math.round(bytes / 1024)))} KB`;
}

/**
 * O que impede a foto de ser aceita, em frase que diz o que fazer — ou `null`.
 *
 * A mesma regra do servidor (lado menor de pelo menos 256 px, conferido na
 * foto JA REDUZIDA), dita antes de subir. Dois casos, porque sao duas saidas:
 * a foto pequena pede outra foto; a panoramica, que so fica estreita DEPOIS de
 * reduzida, pede uma foto menos alongada.
 */
export function problemaDaFoto(largura: number, altura: number): string | null {
  if (!(largura > 0 && altura > 0)) return NAO_ABRE;
  if (Math.min(largura, altura) < LADO_MINIMO_DA_FOTO) {
    return `A foto tem ${descreverMedidas(largura, altura)}. Envie uma com pelo menos ${formatarInteiro(LADO_MINIMO_DA_FOTO)} px no lado menor.`;
  }
  const final = medidasDaFoto(largura, altura);
  const menorFinal = Math.min(final.largura, final.altura);
  if (menorFinal < LADO_MINIMO_DA_FOTO) {
    return `A foto tem ${descreverMedidas(largura, altura)} e, reduzida a ${formatarInteiro(LADO_MAXIMO_DA_FOTO)} px no lado maior, fica com ${formatarInteiro(menorFinal)} px no lado menor. Envie uma foto menos alongada, com pelo menos ${formatarInteiro(LADO_MINIMO_DA_FOTO)} px no lado menor.`;
  }
  return null;
}

/* ---------------------------------------------------------------------------
 * Daqui para baixo, navegador: `canvas`, `createImageBitmap`, `Image`.
 * ------------------------------------------------------------------------- */

export interface FotoPreparada {
  /** O JPEG que sobe para `PUT /equipamentos/:id/foto`. */
  readonly principal: Blob;
  /** O JPEG de 64 x 64 que sobe para `PUT .../foto/miniatura`. */
  readonly miniatura: Blob;
  readonly largura: number;
  readonly altura: number;
  /**
   * Endereco local (`blob:`) da foto preparada, para a previa. Quem recebe
   * revoga com `URL.revokeObjectURL` ao descartar ou ao sair da tela.
   */
  readonly previa: string;
}

interface ImagemAberta {
  readonly fonte: CanvasImageSource;
  readonly largura: number;
  readonly altura: number;
  readonly liberar: () => void;
}

/**
 * Abre o arquivo escolhido, ja endireitado pelo EXIF.
 *
 * `createImageBitmap` primeiro: decodifica fora do fio da tela. Onde ele
 * recusa o formato (o HEIC, no Safari, so abre por `<img>`), cai para
 * `Image.decode()` — e o `<img>` tambem aplica a orientacao, pela regra
 * `image-orientation: from-image` que os navegadores seguem por padrao.
 */
async function abrirImagem(arquivo: Blob): Promise<ImagemAberta> {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(arquivo, { imageOrientation: 'from-image' });
      return {
        fonte: bitmap,
        largura: bitmap.width,
        altura: bitmap.height,
        liberar: () => {
          bitmap.close();
        },
      };
    } catch {
      /* formato que o bitmap nao abre: tenta pelo <img> */
    }
  }

  const endereco = URL.createObjectURL(arquivo);
  const imagem = new Image();
  imagem.decoding = 'async';
  imagem.src = endereco;
  try {
    await imagem.decode();
  } catch {
    URL.revokeObjectURL(endereco);
    throw new ProblemaDaFoto(NAO_ABRE);
  }
  return {
    fonte: imagem,
    largura: imagem.naturalWidth,
    altura: imagem.naturalHeight,
    liberar: () => {
      URL.revokeObjectURL(endereco);
    },
  };
}

interface Retangulo {
  readonly x: number;
  readonly y: number;
  readonly largura: number;
  readonly altura: number;
}

/**
 * Desenha `origem` de `fonte` num canvas novo de `largura` x `altura`.
 *
 * O FUNDO E BRANCO antes do desenho: JPEG nao tem transparencia, e um PNG
 * transparente regravado sem fundo sairia com o fundo preto. `'white'` e o
 * nome da cor do CSS, e nao valor de tela: e o fundo do ARQUIVO gravado, que
 * nao muda com o tema.
 */
function desenhar(fonte: CanvasImageSource, origem: Retangulo, largura: number, altura: number): HTMLCanvasElement {
  const tela = document.createElement('canvas');
  tela.width = largura;
  tela.height = altura;
  const contexto = tela.getContext('2d');
  if (!contexto) throw new ProblemaDaFoto(NAO_ABRE);
  contexto.fillStyle = 'white';
  contexto.fillRect(0, 0, largura, altura);
  contexto.imageSmoothingEnabled = true;
  contexto.imageSmoothingQuality = 'high';
  contexto.drawImage(fonte, origem.x, origem.y, origem.largura, origem.altura, 0, 0, largura, altura);
  return tela;
}

/**
 * Reduz em passos de metade ate ficar a menos do dobro do alvo, e so entao
 * desenha no tamanho final. Reduzir 3.000 px a 64 de uma vez so faz o
 * navegador pular pixel (a miniatura sai serrilhada); pela metade, cada passo
 * tira a media de quatro.
 */
function reduzir(fonte: CanvasImageSource, origem: Retangulo, largura: number, altura: number): HTMLCanvasElement {
  let atual: { fonte: CanvasImageSource; origem: Retangulo } = { fonte, origem };
  while (atual.origem.largura / 2 >= largura && atual.origem.altura / 2 >= altura) {
    const l = Math.round(atual.origem.largura / 2);
    const a = Math.round(atual.origem.altura / 2);
    atual = { fonte: desenhar(atual.fonte, atual.origem, l, a), origem: { x: 0, y: 0, largura: l, altura: a } };
  }
  return desenhar(atual.fonte, atual.origem, largura, altura);
}

function paraJpeg(tela: HTMLCanvasElement, qualidade: number): Promise<Blob | null> {
  return new Promise((resolver) => {
    tela.toBlob(resolver, 'image/jpeg', qualidade);
  });
}

/**
 * Prepara a foto escolhida: abre, confere as medidas, reduz, regrava em JPEG
 * e gera a miniatura. Recusa com `ProblemaDaFoto` o que o servidor recusaria.
 */
export async function prepararFoto(arquivo: File): Promise<FotoPreparada> {
  const aberta = await abrirImagem(arquivo);
  try {
    const problema = problemaDaFoto(aberta.largura, aberta.altura);
    if (problema) throw new ProblemaDaFoto(problema);

    const medidas = medidasDaFoto(aberta.largura, aberta.altura);
    const tela = reduzir(
      aberta.fonte,
      { x: 0, y: 0, largura: aberta.largura, altura: aberta.altura },
      medidas.largura,
      medidas.altura,
    );

    let principal: Blob | null = null;
    for (const qualidade of QUALIDADES) {
      const tentativa = await paraJpeg(tela, qualidade);
      if (tentativa === null) throw new ProblemaDaFoto(NAO_ABRE);
      principal = tentativa;
      if (tentativa.size <= LIMITE_DA_FOTO_BYTES) break;
    }
    if (principal === null || principal.size > LIMITE_DA_FOTO_BYTES) {
      throw new ProblemaDaFoto(
        `A foto passa de ${formatarInteiro(LIMITE_DA_FOTO_BYTES / (1024 * 1024))} MB mesmo reduzida. Envie outra foto da máquina, com menos detalhe de fundo.`,
      );
    }

    /* A miniatura sai da foto JA PREPARADA, e nao do arquivo original: e o
       recorte da mesma imagem que fica guardada, e o servidor a amarra ao
       SHA-256 dela. */
    const quadrado = recorteQuadrado(medidas.largura, medidas.altura);
    const telaDaMiniatura = reduzir(
      tela,
      { x: quadrado.x, y: quadrado.y, largura: quadrado.lado, altura: quadrado.lado },
      LADO_DA_MINIATURA,
      LADO_DA_MINIATURA,
    );
    const miniatura = await paraJpeg(telaDaMiniatura, QUALIDADE_JPEG);
    if (miniatura === null) throw new ProblemaDaFoto(NAO_ABRE);

    return {
      principal,
      miniatura,
      largura: medidas.largura,
      altura: medidas.altura,
      previa: URL.createObjectURL(principal),
    };
  } finally {
    aberta.liberar();
  }
}
